import { useCallback } from 'react';
import { updatePartnerFields } from '@/data/partnersRepo';
import type { HistoryInfoType } from '@/types/types_history';
import { useCurrentUserSimple } from './useCurrentUserSimple';
import { applyHistoryToUpdate, type HistorizedSaveType } from './util_history';
import type { SaveStack } from './util_saveStack';

/**
 * Returns an `autosave(userAction, fields, currentHistory)` function that writes
 * changed fields of one partner to Firestore, registering the operation on the
 * save stack. Mirrors useAutosaveExperience.
 *
 * This is the single write path for partner field edits, used by both the
 * admin form (which autosaves on blur) and the partner's own profile page
 * (which saves a card at a time on a button). The interaction models differ on
 * purpose; the audit trail must not, so the history step is appended here
 * rather than at the call site.
 */
export function useAutosavePartner(partnerId: string | undefined, saveStack: SaveStack) {
  const userSimple = useCurrentUserSimple();

  return useCallback(
    (
      userAction: string,
      fields: Record<string, unknown>,
      currentHistory: HistoryInfoType | undefined,
      isUndoStep = false,
    ): HistorizedSaveType => {
      if (!partnerId) {
        throw new Error(`Cannot autosave: no partner id. action=[${userAction}]`);
      }
      const { updateObj, newHistory } = applyHistoryToUpdate(
        fields,
        userAction,
        currentHistory,
        userSimple,
        isUndoStep,
      );

      const opId = crypto.randomUUID();
      saveStack.pushToSaveStack({ id: opId, userAction });
      const saved = updatePartnerFields(partnerId, updateObj).then(() => {
        saveStack.removeFromSaveStack(opId);
      });

      return { newHistory, saved };
    },
    [partnerId, saveStack, userSimple],
  );
}
